/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button as BsButton } from 'react-bootstrap'
import { useState, useEffect, useRef } from 'react'
import { OrderLogs } from 'components/organisms/log/OrderLogs'

import { skipToken } from '@reduxjs/toolkit/query/react'

import MsgPanel from 'components/organisms/message/Panel'
import NotePanel from 'components/organisms/note/Panel'
import { AutomatedSms, Message, Note, PayerNotes } from 'store/types'
import { usePinNoteMutation } from 'store/services/notes'
import { useSendSmsToPatientMutation, useUpdateSmsIgnoreTimezoneMutation } from 'store/services/labOrder'
import { useIsPatientOptedOutQuery } from 'store/services/messages'
import Toast from 'components/atoms/Toast'

const StyledLink = styled.span`
  margin-bottom: -2px !important;
  &.active {
    &:hover {
      border-bottom: 1px solid white !important;
      margin-bottom: -2px !important;
    }
  }
`

const StyledTabBody = styled.div`
  min-height: 200px;

  .msg_panel {
    min-height: unset;

    .cs-message-list__scroll-wrapper {
      max-height: 40vh;
    }
  }
`

const MailButton = styled(BsButton)`
  padding: 0 2px;
`

const automatedSmsCardStyles = css`
  position: relative;
  background: #eef6ff;
  border: 1px solid #b7d4f6;
`

const automatedSmsLabelStyles = css`
  color: #1f5f99;
  font-weight: 600;
`

const automatedScheduledSmsLabelStyles = css`
  color: #9a6700;
  font-weight: 600;
`

const SmsTabLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 320px;
  height: min(60vh, 600px);
  padding: 1rem;
`

const SmsMessagesScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  padding-right: 0.25rem;
  margin-bottom: 1rem;
`

const SmsMessageText = styled.p`
  margin: 0.25rem 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const SmsOptOutWarning = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  color: #842029;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 0.375rem;
`

interface IProps {
  orderId: number
  isOpen: boolean
  tab: string
  setTab: (v: string) => void
  className?: string
  messages?: Message[]
  automatedSmsMessages?: AutomatedSms[]
  addMessage: (m: string) => Promise<void>
  notes?: Note[]
  addNote: (m: string, type?: string) => Promise<void>
  payerNotes?: PayerNotes
  deleteNote?: (param: { uuid: string }) => Promise<void>
  updateMessageReadStatus: (params: {
    uuid: string
    readStatus: 'READ' | 'UNREAD'
  }) => Promise<void>
  canMarkMessagesAsRead?: boolean
  smsIgnoreTimezone?: boolean
}

export const TabControl = ({
  orderId,
  isOpen,
  tab,
  setTab,
  className,
  addMessage,
  addNote,
  messages,
  automatedSmsMessages,
  notes,
  deleteNote,
  updateMessageReadStatus,
  canMarkMessagesAsRead = true,
  payerNotes,
  smsIgnoreTimezone,
}: IProps) => {
  const [pinNote] = usePinNoteMutation()
  const [sendSmsToPatient, { isLoading: isSendingSms }] = useSendSmsToPatientMutation()
  const [updateSmsIgnoreTimezone, { isLoading: isUpdatingSmsPrefs }] =
    useUpdateSmsIgnoreTimezoneMutation()
  const { data: optOutStatus } = useIsPatientOptedOutQuery(
    orderId ? { orderId: Number(orderId) } : skipToken,
    { refetchOnFocus: false },
  )
  const smsScrollRef = useRef<HTMLDivElement>(null)
  const smsBottomRef = useRef<HTMLDivElement>(null)

  const handlePinNote = async (params: { uuid: string; orderId: string; pinned: boolean }) => {
    try {
      await pinNote(params).unwrap()
    } catch (error) {
      console.error('Failed to pin note:', error)
    }
  }

  const [openedSmsMessages, setOpenedSmsMessages] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (messages) {
      const readMessages = messages
        .filter((msg) => msg.sentBy === 'patient' && msg.sentTo === 'lab' && msg.readAt)
        .map((msg) => msg.uuid)
      setOpenedSmsMessages(new Set(readMessages))
    }
  }, [messages])

  const [smsText, setSmsText] = useState('')
  const [ignoreTimezone, setIgnoreTimezone] = useState<boolean>(smsIgnoreTimezone ?? false)

  useEffect(() => {
    setIgnoreTimezone(smsIgnoreTimezone ?? false)
  }, [smsIgnoreTimezone])

  useEffect(() => {
    if (tab !== 'sms' || !isOpen) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      const scrollElement = smsScrollRef.current

      if (scrollElement) {
        scrollElement.scrollTo({
          top: scrollElement.scrollHeight,
          behavior: 'smooth',
        })
      } else {
        smsBottomRef.current?.scrollIntoView({
          block: 'end',
          behavior: 'smooth',
        })
      }
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [isOpen, tab])

  const nonPatientMessages =
    messages?.filter(
      (msg) =>
        (msg.sentBy === 'dme' && msg.sentTo === 'lab') ||
        (msg.sentBy === 'lab' && msg.sentTo === 'dme'),
    ) || []
  const unreadMessages = nonPatientMessages?.reduce(
    (agg, curr) => (curr.sentBy === 'lab' || curr.readAt ? agg : agg + 1),
    0,
  )
  const smsMessages =
    messages?.filter(
      (msg) =>
        (msg.sentBy === 'patient' && msg.sentTo === 'lab') ||
        (msg.sentBy === 'lab' && msg.sentTo === 'patient'),
    ) || []
  const unreadSmsMessages = smsMessages?.reduce(
    (agg, curr) => (curr.sentBy === 'lab' || curr.readAt ? agg : agg + 1),
    0,
  )
  const smsConversation = [
    ...smsMessages.map((message, index) => ({
      key: message.uuid,
      sortOrder: index,
      type: 'manual' as const,
      date: message.createdAt,
      message,
    })),
    ...(automatedSmsMessages || []).map((message, index) => ({
      key: `automated-${message.date}-${index}`,
      sortOrder: smsMessages.length + index,
      type: 'automated' as const,
      date: message.date,
      message,
    })),
  ].sort(
    (a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime() || a.sortOrder - b.sortOrder,
  )

  const openSmsMessage = async (uuid: string) => {
    const message = messages?.find((msg) => msg.uuid === uuid && msg.sentBy === 'patient')
    const isFirstOpen = !openedSmsMessages.has(uuid)
    const isRead = message?.readAt != null
    const needsReadStatusUpdate = message && !isRead && isFirstOpen

    if (needsReadStatusUpdate) {
      try {
        await updateMessageReadStatus({
          uuid,
          readStatus: 'READ',
        })

        setOpenedSmsMessages((prev) => {
          const newSet = new Set(prev)
          newSet.add(uuid)
          return newSet
        })
      } catch (error) {
        console.error('Failed to mark SMS as read:', error)
      }
    }

    if (isRead && isFirstOpen) {
      setOpenedSmsMessages((prev) => {
        const newSet = new Set(prev)
        newSet.add(uuid)
        return newSet
      })
    }
  }

  const handleSendSms = async () => {
    if (!smsText.trim()) return

    try {
      await sendSmsToPatient({
        orderId: Number(orderId),
        message: smsText,
      }).unwrap()

      setSmsText('')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      Toast({ type: 'error', label: `Failed to send SMS: ${errorMessage}` })
    }
  }

  const handleToggleIgnoreTimezone = async (checked: boolean) => {
    setIgnoreTimezone(checked)
    try {
      await updateSmsIgnoreTimezone({ orderId: Number(orderId), ignoreTimezone: checked }).unwrap()
    } catch (error) {
      setIgnoreTimezone(!checked)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      Toast({ type: 'error', label: `Failed to update preference: ${errorMessage}` })
    }
  }

  return (
    <div className={`tabs ${className}`}>
      <ul className="nav nav-tabs nav-fill">
        <li className="nav-item" onClick={() => setTab('notes')} style={{ cursor: 'pointer' }}>
          <StyledLink
            className={`py-3 font-weight-bold nav-link tabs-header ${
              tab === 'notes' ? 'textDark active nav-tabs-no-bckg' : ''
            }`}>
            Notes
          </StyledLink>
        </li>
        <li
          className="nav-item"
          onClick={() => setTab('schedulingNotes')}
          style={{ cursor: 'pointer' }}>
          <StyledLink
            className={`py-3 font-weight-bold nav-link tabs-header ${
              tab === 'schedulingNotes' ? 'textDark active nav-tabs-no-bckg' : ''
            }`}>
            Scheduling Notes
          </StyledLink>
        </li>
        <li className="nav-item" onClick={() => setTab('messages')} style={{ cursor: 'pointer' }}>
          <StyledLink
            className={`py-3 font-weight-bold nav-link tabs-header ${
              tab === 'messages' ? 'textDark active nav-tabs-no-bckg' : ''
            }`}>
            Messages{' '}
            {unreadMessages ? (
              <span
                className="badge bg-danger right"
                css={css`
                  width: 18px;
                  height: 18px;
                  font-size: 0.85rem;
                `}
                style={{ marginLeft: 6, borderRadius: '47%' }}>
                {unreadMessages}
              </span>
            ) : (
              <></>
            )}
          </StyledLink>
        </li>
        <li className="nav-item" onClick={() => setTab('sms')} style={{ cursor: 'pointer' }}>
          <StyledLink
            className={`py-3 font-weight-bold nav-link tabs-header ${
              tab === 'sms' ? 'textDark active nav-tabs-no-bckg' : ''
            }`}>
            SMS
            {unreadSmsMessages ? (
              <span
                className="badge bg-danger right"
                css={css`
                  width: 18px;
                  height: 18px;
                  font-size: 0.85rem;
                `}
                style={{ marginLeft: 6, borderRadius: '47%' }}>
                {unreadSmsMessages}
              </span>
            ) : (
              <></>
            )}
          </StyledLink>
        </li>
        <li className="nav-item" onClick={() => setTab('logs')} style={{ cursor: 'pointer' }}>
          <StyledLink
            className={`py-3 font-weight-bold nav-link tabs-header ${
              tab === 'logs' ? 'textDark active nav-tabs-no-bckg' : ''
            }`}>
            Logs
          </StyledLink>
        </li>
      </ul>
      <div className="tab-content tabs_container bg-white">
        <StyledTabBody className={`tab-pane fade ${tab === 'messages' ? 'show active' : ''}`}>
          <MsgPanel
            updateMessageReadStatus={updateMessageReadStatus}
            userContext="LAB"
            messages={nonPatientMessages}
            addMessage={addMessage}
            canMarkMessagesAsRead={canMarkMessagesAsRead}
          />
        </StyledTabBody>
        <StyledTabBody
          className={`tab-pane fade ${tab === 'schedulingNotes' ? 'show active' : ''}`}>
          <NotePanel
            type="scheduling"
            userContext="LAB"
            notes={notes}
            addNote={addNote}
            pinNote={handlePinNote}
            deleteNote={deleteNote}
            orderId={orderId.toString()}
          />
        </StyledTabBody>
        <StyledTabBody className={`tab-pane fade ${tab === 'notes' ? 'show active' : ''}`}>
          <NotePanel
            userContext="LAB"
            notes={notes}
            addNote={addNote}
            pinNote={handlePinNote}
            deleteNote={deleteNote}
            payerNotes={payerNotes}
            orderId={orderId.toString()}
          />
        </StyledTabBody>
        <StyledTabBody className={`tab-pane fade ${tab === 'sms' ? 'show active' : ''}`}>
          <SmsTabLayout>
            <SmsMessagesScrollArea ref={smsScrollRef}>
              {smsConversation.length > 0 ? (
                <>
                  {smsConversation.map((entry) => {
                    if (entry.type === 'automated') {
                      return (
                        <div
                          key={entry.key}
                          className="card mb-2"
                          css={automatedSmsCardStyles}>
                          <div className="card-body p-2">
                            <div className="d-flex justify-content-between align-items-start">
                              <div
                                className="flex-grow-1"
                                css={css`
                                  min-width: 0;
                                `}>
                                <small className="text-muted d-block">
                                  To: Patient • Automated • {new Date(entry.date).toLocaleString()}
                                </small>
                                <SmsMessageText>{entry.message.text}</SmsMessageText>
                              </div>
                              <div className="d-flex flex-column align-items-end ml-2">
                                <small
                                  css={
                                    entry.message.scheduled
                                      ? automatedScheduledSmsLabelStyles
                                      : automatedSmsLabelStyles
                                  }>
                                  <i className={`fa ${entry.message.scheduled ? 'fa-clock' : 'fa-check'}`} />{' '}
                                  {entry.message.scheduled
                                    ? 'Scheduled in patient timezone'
                                    : 'Sent automatically'}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    }

                    const message = entry.message
                    const isOpened = openedSmsMessages.has(message.uuid)
                    const isIncoming = message.sentBy === 'patient'
                    const isOutgoing = message.sentBy === 'lab'
                    return (
                      <div
                        key={entry.key}
                        className={`card mb-2 ${isOutgoing ? 'bg-light' : ''}`}
                        css={css`
                          position: relative;
                        `}>
                        <div className="card-body p-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div
                              className="flex-grow-1"
                              css={css`
                                min-width: 0;
                              `}>
                              <small className="text-muted d-block">
                                {isIncoming
                                  ? `From: ${message.phoneNumber || 'Unknown'}`
                                  : 'To: Patient'}{' '}
                                • {new Date(entry.date).toLocaleString()}
                              </small>
                              {isIncoming ? (
                                isOpened ? (
                                  <SmsMessageText>{message.text}</SmsMessageText>
                                ) : (
                                  <div className="d-flex justify-content-center w-100 mt-2">
                                    <MailButton
                                      size="sm"
                                      onClick={() => openSmsMessage(message.uuid)}>
                                      <i className="fa fa-envelope" /> Open
                                    </MailButton>
                                  </div>
                                )
                              ) : (
                                <SmsMessageText>{message.text}</SmsMessageText>
                              )}
                            </div>
                            <div className="d-flex flex-column align-items-end ml-2">
                              {isIncoming && (
                                <>
                                  <small className={isOpened ? 'text-success' : 'text-muted'}>
                                    {isOpened ? 'Read' : 'Unread'}
                                  </small>
                                  {isOpened && (
                                    <i
                                      className="fas fa-envelope-open"
                                      css={css`
                                        color: #6c757d;
                                        font-size: 0.8rem;
                                        margin-top: 4px;
                                      `}
                                    />
                                  )}
                                </>
                              )}
                              {isOutgoing && (
                                <small className="text-success">
                                  <i className="fa fa-check" /> Sent
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={smsBottomRef} />
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fa fa-mobile fa-2x mb-2"></i>
                  <p>No SMS messages received yet</p>
                </div>
              )}
            </SmsMessagesScrollArea>

            <div>
              {!!optOutStatus?.isOptedOut && (
                <SmsOptOutWarning>
                  NOTE: This patient&apos;s phone number has been opted out of receiving SMS
                  messages. Messages sent here will not be delivered.
                </SmsOptOutWarning>
              )}
              <div className="mb-2">
                <label className="form-label">Add a message</label>
              </div>
              <textarea
                className="form-control mb-2"
                placeholder="Describe your request..."
                rows={3}
                value={smsText}
                onChange={(e) => setSmsText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendSms()}
              />
              <div className="d-flex justify-content-between align-items-center">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="smsIgnoreTimezone"
                    checked={ignoreTimezone}
                    onChange={(e) => handleToggleIgnoreTimezone(e.target.checked)}
                    disabled={isUpdatingSmsPrefs}
                  />
                  <label className="form-check-label" htmlFor="smsIgnoreTimezone">
                    Send even after daytime hours
                  </label>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSendSms}
                  disabled={!smsText.trim() || isSendingSms}>
                  {isSendingSms ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </SmsTabLayout>
        </StyledTabBody>
        <StyledTabBody className={`tab-pane fade ${tab === 'logs' ? 'show active' : ''}`}>
          <OrderLogs orderId={orderId} visible={tab === 'logs'} />
        </StyledTabBody>
      </div>
    </div>
  )
}
